import { inject } from '@angular/core';
import {
  RedirectCommand,
  Router,
  type ActivatedRouteSnapshot,
  type ResolveFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { getParamsObject } from '@cosmos/core';
import { injectWindow } from '@cosmos/util-common/di';
import {
  CustomerPortalAuthActions,
  isPresentationUuid,
} from '@customer-portal/auth/data-access-auth';
import { PreviewService } from '@customer-portal/data-access-preview';
import {
  CustomerPortalDistributorQueries,
  CustomerPortalPresentationQueries,
} from '@customer-portal/presentations/data-access-presentations';
import {
  CustomerPortalDistributorActions,
  CustomerPortalPresentationActions,
} from '@customer-portal/presentations/data-access-presentations/actions';
import {
  CustomerPortalProjectActions,
  CustomerPortalProjectQueries,
} from '@customer-portal/projects/data-access/store';
import { dispatchPdfDocumentError } from '@esp/common/util-pdf-generation';

import { injectTemplateFontHelper } from '../utils/presentation.util';

/**
 * This resolver is intended to be used with SSR to load all needed data eagerly to
 * render presentation print version.
 */
export const resolvePresentationPrint: ResolveFn<unknown> = async (
  route: ActivatedRouteSnapshot
) => {
  const store = inject(Store);
  const router = inject(Router);
  const { presentationId } = getParamsObject(route.paramMap, {
    required: ['presentationId'],
  })!;
  const previewService = inject(PreviewService);
  const templateFontHelper = injectTemplateFontHelper();
  const window = injectWindow();

  // Every failure below leaves the print page for `/error`, so the PDF worker
  // would wait for its ready selector until it times out — abort it right away.
  const redirectToErrorPage = (error: unknown, fallbackStatusCode?: number) => {
    dispatchPdfDocumentError(window, error, fallbackStatusCode);
    return new RedirectCommand(router.parseUrl('/error'), {
      skipLocationChange: true,
    });
  };

  // A standalone presentation is shared as `/presentations/{uuid}` with no
  // access code; a project one keeps its numeric id. Both arrive as strings,
  // so only the shape of the value tells them apart — and a uuid must not be
  // parsed, or every call below would be made with `NaN`.
  const isUuid = isPresentationUuid(presentationId);
  const presentationKey: string | number = isUuid
    ? presentationId
    : parseInt(presentationId, 10);

  if (previewService.useMockProjectAndPresentation) {
    const presentation = store.selectSnapshot(
      CustomerPortalPresentationQueries.getPresentation
    );
    const matchesRoute = isUuid
      ? presentation?.Uuid === presentationId
      : presentation?.Id === presentationKey;
    if (matchesRoute) {
      return;
    }
  }

  const getDistributorAndDesignActions = (presentationKey: number) => [
    new CustomerPortalDistributorActions.GetDistributor(presentationKey),
    new CustomerPortalDistributorActions.GetDesign(presentationKey),
  ];

  // At this moment we already have authenticated user.
  // Skip template loading here to avoid blocking the parallel batch.
  // Skip quote loading because it's not needed for print version rendering.
  try {
    const actions: Array<object> = [
      new CustomerPortalPresentationActions.GetPresentation({
        presentationId: presentationKey,
        shouldLoadTemplate: false,
        shouldLoadQuote: false,
      }),
    ];

    // The distributor and the design are addressed by the presentation's
    // numeric id, which a uuid route does not know until the presentation
    // itself is loaded — those two are dispatched below instead.
    if (!isUuid) {
      actions.push(
        ...getDistributorAndDesignActions(presentationKey as number)
      );
    }

    await firstValueFrom(store.dispatch(actions), { defaultValue: undefined });
  } catch (error) {
    // A uuid route carries its own secret, so the session can be thrown away
    // and rebuilt from the link alone. A stored token the server no longer
    // accepts for this presentation keeps failing otherwise, since every guard
    // short-circuits on the session it finds.
    if (isUuid) {
      store.dispatch(
        new CustomerPortalAuthActions.ClearSession({ presentationId })
      );
    }
    return redirectToErrorPage(error);
  }

  const presentation = store.selectSnapshot(
    CustomerPortalPresentationQueries.getPresentation
  );

  if (!presentation) {
    return redirectToErrorPage(null, 404);
  }

  if (isUuid) {
    // Now that the presentation is loaded, its numeric id is known. Awaited
    // rather than fired off: the design is rendered into the printed page.
    try {
      await firstValueFrom(
        store.dispatch(getDistributorAndDesignActions(presentation.Id)),
        { defaultValue: undefined }
      );
    } catch (error) {
      return redirectToErrorPage(error);
    }
  }

  // Start preloading product images immediately so they're cached when DOM renders.
  presentation.Products.forEach((product) =>
    preloadImage(product.DefaultMedia?.Url)
  );

  const design = store.selectSnapshot(
    CustomerPortalDistributorQueries.getDesign
  );
  // Preload design image early since it's likely above-the-fold content.
  preloadImage(design?.ImageUrl);

  const actions: Array<object> = [];

  // Project is not needed for standalone presentations
  if (presentation.ProjectId) {
    actions.push(
      new CustomerPortalProjectActions.GetProject(presentation.ProjectId)
    );
  }

  // Only fetch template if it exists to avoid unnecessary 404 requests.
  if (presentation.PresentationTemplateId) {
    actions.push(
      new CustomerPortalPresentationActions.GetPresentationTemplate(
        presentation.PresentationTemplateId
      )
    );
  }

  // Load remaining dependencies after we've confirmed presentation exists.
  try {
    await firstValueFrom(store.dispatch(actions), { defaultValue: undefined });
  } catch (error) {
    return redirectToErrorPage(error);
  }

  const bannerData = store.selectSnapshot(
    CustomerPortalPresentationQueries.getPageBannerData
  );
  // Preload banner image as it's typically hero content with high visual priority.
  preloadImage(bannerData?.image?.FileUrl);

  const project = store.selectSnapshot(CustomerPortalProjectQueries.getProject);
  // Preload customer logo for brand consistency on initial render.
  preloadImage(project?.Customer.LogoImageUrl);

  // Preload template custom font if it's not the default one Inter.
  // Wait until it's loaded before showing the presentation
  // so we can rely on document.fonts.ready to ensure the font is available for print rendering.
  await templateFontHelper.preloadTemplateFont();

  return true;
};

// Fire-and-forget approach prioritizes fast resolver completion over guaranteed image loading.
// No error handling needed since missing images won't break page functionality.
export function preloadImage(url: string | undefined): void {
  if (url) {
    const image = new Image();
    image.src = url;
  }
}
